import { useCallback, useState } from 'react';
import { View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { CreateResponsiveStyle, DEVICE_SIZES, minSize } from 'rn-responsive-styles';

import { Button, LoadingOverlay, TabsContainer, Text, spacing } from '@pletly/ui';
import { OrganisationMemberDataType } from './components/OrganisationMembersTable';
import { CareRecipientsTable } from './components/CareRecipientsTable';
import { CaregiversTable } from './components/CaregiversTable';
import { ArchivedTable } from './components/ArchivedTable';
import {
	TemporaryAccessTableDataType,
	useOrganisationData,
} from './components/useOrganisationData';
import { RootStackScreenProps } from '../../navigation/types';
import { withTranslation } from '../../locales/hoc';
import { TeamsCard } from '../components/TeamsCard';
import { FamilyCaregiversTable } from './components/FamilyCaregiversTable';
import { ConnectionRequestsCard } from './components/ConnectionRequestsCard';
import { TemporaryAccessTable } from './components/TemporaryAccessTable';
import { useOrganisationInfo } from '../../context';
import { OrganisationRole } from '../../__generated__/types';
import { useGetInvitationsFromFamilyCaregiversQuery } from '../invitations/components/__generated__/invitationsFromFamilyCaregivers.generated';

type Props = RootStackScreenProps<'OrganisationView' | 'Home'>;

const ALLOWED_ROLES: { [key in 'read' | 'update']: OrganisationRole[] } = {
	read: [OrganisationRole.Admin, OrganisationRole.Manager],
	update: [OrganisationRole.Admin],
};

const Organisation = ({
	route: {
		params: { uuid },
	},
}: Props) => {
	const { t } = useTranslation();
	const styles = useStyles();
	const { navigate } = useNavigation();
	const { loading, organisation } = useOrganisationData({ uuid });
	const { data } = useGetInvitationsFromFamilyCaregiversQuery({
		variables: { organisationUuid: uuid },
	});
	const { organisationInfo } = useOrganisationInfo();
	const [tabIndex, setTabIndex] = useState(0);

	const handleSettingsPress = useCallback(() => {
		navigate('EditOrganisation', { uuid });
	}, [uuid, navigate]);

	if (loading) {
		return <LoadingOverlay show />;
	}

	if (!organisation || !organisationInfo) {
		return <Text>{t('organisation-not-found')}</Text>;
	}

	if (!ALLOWED_ROLES.read.includes(organisationInfo.role)) {
		return null;
	}

	const tabs = getTabs(organisation, t);

	return (
		<View>
			<View style={styles.container}>
				<View style={styles.mainContent}>
					<View style={styles.header}>
						<Text category="h3">{organisation.name}</Text>

						<View style={styles.headerActions}>
							<Button color="primary" variant="outline" onPress={() => navigate('NewPost')}>
								{t('new-post')}
							</Button>
							{ALLOWED_ROLES.update.includes(organisationInfo.role) && (
								<Button variant="filled" color="basic" onPress={handleSettingsPress}>
									{t('settings')}
								</Button>
							)}
						</View>
					</View>
					<View>
						<TabsContainer tabs={tabs} currentIndex={tabIndex} onIndexChange={setTabIndex} />
					</View>
				</View>

				<View style={styles.sideSection}>
					<TeamsCard organisationUuid={uuid} teams={organisation.teams} />
					<ConnectionRequestsCard
						organisationUuid={uuid}
						pendingInvitationsCount={data?.invitationsFromFamilyCaregivers.length}
					/>
				</View>
			</View>
		</View>
	);
};

export const OrganisationPage = withTranslation(Organisation);

const useStyles = CreateResponsiveStyle(
	{
		container: {
			paddingVertical: spacing(4),
			width: '100%',
			flexWrap: 'wrap-reverse',
			justifyContent: 'center',
			maxWidth: 1180,
			marginHorizontal: 'auto',
			flexDirection: 'row',
			gap: spacing(4),
			padding: spacing(),
		},
		mainContent: {
			flex: 1,
			minWidth: 400,
			gap: spacing(3),
		},
		header: {
			flexDirection: 'row',
			justifyContent: 'space-between',
			alignItems: 'center',
		},
		headerActions: {
			flexDirection: 'row',
			gap: spacing(2),
		},
		sideSection: {
			gap: spacing(4),
		},
	},
	{
		[minSize(DEVICE_SIZES.LARGE_DEVICE)]: {
			container: {
				maxWidth: 1280,
				padding: 0,
			},
		},
		[minSize(DEVICE_SIZES.MEDIUM_DEVICE)]: {
			container: {
				flexWrap: 'wrap',
			},
		},
	},
);

const getTabs = (
	organisation: {
		uuid: string;
		members: {
			caregivers: OrganisationMemberDataType[];
			familyCaregivers: OrganisationMemberDataType[];
			careRecipients: OrganisationMemberDataType[];
			archived: OrganisationMemberDataType[];
		};
		temporaryAccess: TemporaryAccessTableDataType[];
	},
	t: TFunction,
) => {
	const {
		uuid,
		members: { archived, caregivers, familyCaregivers, careRecipients },
		temporaryAccess,
	} = organisation;

	return [
		{
			name: 'caregivers',
			label: t('tab-label-caregivers'),
			component: <CaregiversTable data={caregivers} organisationUuid={uuid} />,
		},
		{
			name: 'familyCaregivers',
			label: t('tab-label-familycaregivers'),
			component: <FamilyCaregiversTable data={familyCaregivers} />,
		},
		{
			name: 'careRecipients',
			label: t('tab-label-care-recipients'),
			component: <CareRecipientsTable data={careRecipients} organisationUuid={uuid} />,
		},
		{
			name: 'temporary-access',
			label: t('tab-label-temporary-access'),
			component: <TemporaryAccessTable data={temporaryAccess} organisationUuid={uuid} />,
		},
		{
			name: 'archived',
			label: t('tab-label-archived'),
			component: <ArchivedTable data={archived} />,
		},
	];
};
